import { useMemo, useState } from "react";

import type { AdminTeamScope, AdminTeamTournament } from "../lib/api";
import {
  chosenCountLabel,
  filterTeamTournaments,
  isClosedLike,
  sortTeamTournaments,
  teamTournamentDates,
  teamTournamentStatusLabel,
} from "../lib/teamScope";

// A qué torneos entra un miembro del Equipo (Eduardo 2026-09-29): todos
// (también los que el organizador cree después) o solo los que se marquen.
// Controlado: no guarda nada por sí solo; quien lo usa decide cuándo enviar.
// Sin .form-message/.form-error adentro: los avisos de la página no deben
// volver a saltar como toast al filtrar o marcar la lista.

export interface TeamScopeValue {
  scope: AdminTeamScope;
  selectedIds: number[];
}

interface Props {
  /** Prefijo único para los name/id de los radios (hay varios en la página). */
  idPrefix: string;
  value: TeamScopeValue;
  tournaments: AdminTeamTournament[];
  onChange: (next: TeamScopeValue) => void;
  /** Cualquier interacción (limpia los avisos viejos de la página). */
  onInteract?: () => void;
  disabled?: boolean;
  /** Nombre de la persona, para las etiquetas accesibles. */
  personLabel?: string;
}

/** Con más de estos torneos aparece el buscador. */
export const TEAM_SCOPE_SEARCH_THRESHOLD = 6;

export default function TeamTournamentScopePicker({
  idPrefix,
  value,
  tournaments,
  onChange,
  onInteract,
  disabled = false,
  personLabel,
}: Props) {
  const [query, setQuery] = useState("");
  const ordered = useMemo(() => sortTeamTournaments(tournaments), [tournaments]);
  const visible = useMemo(() => filterTeamTournaments(ordered, query), [ordered, query]);
  const selected = useMemo(() => new Set(value.selectedIds), [value.selectedIds]);
  const hasTournaments = tournaments.length > 0;
  const showSearch = tournaments.length > TEAM_SCOPE_SEARCH_THRESHOLD;
  const firstClosedIndex = visible.findIndex((t) => isClosedLike(t.status));
  const showClosedHeading = firstClosedIndex > 0;
  const chosenCount = tournaments.filter((t) => selected.has(t.id)).length;

  function change(next: TeamScopeValue) {
    onInteract?.();
    onChange(next);
  }

  function toggle(id: number) {
    const nextIds = selected.has(id) ? value.selectedIds.filter((item) => item !== id) : [...value.selectedIds, id];
    change({ scope: "selected", selectedIds: nextIds });
  }

  const groupLabel = personLabel ? `Torneos de ${personLabel}` : "Torneos";

  return (
    <fieldset className="team-scope-picker" disabled={disabled}>
      <legend className="team-scope-picker__legend">¿A qué torneos entra?</legend>
      <div className="team-scope-picker__options" role="radiogroup" aria-label={groupLabel}>
        <label className="team-scope-picker__option">
          <input
            type="radio"
            name={`${idPrefix}-scope`}
            checked={value.scope === "all"}
            onChange={() => change({ scope: "all", selectedIds: value.selectedIds })}
          />
          <span>Todos mis torneos (también los que crees después)</span>
        </label>
        <label className="team-scope-picker__option">
          <input
            type="radio"
            name={`${idPrefix}-scope`}
            checked={value.scope === "selected"}
            disabled={!hasTournaments}
            onChange={() => change({ scope: "selected", selectedIds: value.selectedIds })}
          />
          <span>Solo estos torneos</span>
        </label>
      </div>

      {!hasTournaments ? (
        <p className="team-scope-picker__note muted">
          Aún no tienes torneos. Con «Todos mis torneos» entrará solo a los que crees.
        </p>
      ) : null}

      {value.scope === "selected" && hasTournaments ? (
        <div className="team-scope-picker__panel">
          <div className="team-scope-picker__toolbar">
            {showSearch ? (
              <input
                type="search"
                className="team-scope-picker__search"
                placeholder="Buscar torneo…"
                aria-label="Buscar torneo"
                value={query}
                onChange={(event) => {
                  onInteract?.();
                  setQuery(event.target.value);
                }}
                onKeyDown={(event) => {
                  // Enter (o «Buscar/Ir» del teclado del celular) solo busca:
                  // no debe enviar el formulario de alta con la selección a
                  // medias (crearía la cuenta y mandaría el correo).
                  if (event.key === "Enter") event.preventDefault();
                }}
                enterKeyHint="search"
              />
            ) : null}
            <small className="team-scope-picker__count" aria-live="polite">
              {chosenCountLabel(chosenCount)}
            </small>
          </div>
          <ul className="team-scope-picker__list" aria-label={groupLabel}>
            {visible.map((tournament, index) => {
              const closedLike = isClosedLike(tournament.status);
              const statusLabel = teamTournamentStatusLabel(tournament.status);
              return (
                <li key={tournament.id}>
                  {showClosedHeading && index === firstClosedIndex ? (
                    <p className="team-scope-picker__group">Cerrados y archivados</p>
                  ) : null}
                  <label className={`team-scope-picker__item${closedLike ? " team-scope-picker__item--closed" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(tournament.id)}
                      onChange={() => toggle(tournament.id)}
                    />
                    <span className="team-scope-picker__text">
                      <span className="team-scope-picker__name">{tournament.name}</span>
                      <span className="team-scope-picker__meta">
                        {teamTournamentDates(tournament)}
                        {statusLabel ? (
                          <>
                            {" · "}
                            <span className={closedLike ? "team-scope-picker__status team-scope-picker__status--closed" : "team-scope-picker__status"}>
                              {statusLabel}
                            </span>
                          </>
                        ) : null}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
            {visible.length === 0 ? (
              <li className="team-scope-picker__empty muted">Ningún torneo coincide con «{query.trim()}».</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </fieldset>
  );
}
